import clsx from 'clsx'

interface HeroProps {
  scrollY: number
}

export default function Hero({ scrollY }: HeroProps) {
  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Deep celestial gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0618] via-brand-dark via-[40%] to-brand-mid z-0" />

      {/* Constellation stars */}
      <div className="absolute inset-0 constellation z-[1]" />

      {/* Grain overlay */}
      <div className="absolute inset-0 opacity-[0.06] grain-overlay z-[1]" />

      {/* Breathing orbs */}
      <div
        className="absolute w-[350px] h-[350px] rounded-full top-[8%] right-[25%] z-[1] animate-breathe"
        style={{
          background: 'radial-gradient(circle, rgba(201,150,44,0.18) 0%, rgba(201,150,44,0.05) 40%, transparent 70%)',
          transform: `translateY(${scrollY * 0.06}px)`,
        }}
      />
      <div
        className="absolute w-[250px] h-[250px] rounded-full bottom-[15%] left-[3%] z-[1] animate-breathe-slow"
        style={{
          background: 'radial-gradient(circle, rgba(123,82,171,0.2) 0%, rgba(123,82,171,0.05) 40%, transparent 70%)',
          transform: `translateY(${scrollY * -0.04}px)`,
        }}
      />

      {/* Fixed navbar */}
      <div
        className={clsx(
          'fixed top-0 left-0 right-0 py-3.5 px-6 flex justify-between items-center z-[100] transition-all duration-500',
          scrollY > 80
            ? 'bg-[#0A0618]/90 backdrop-blur-[16px] shadow-[0_1px_0_rgba(201,150,44,0.1)]'
            : 'bg-transparent backdrop-blur-none'
        )}
      >
        <span className="font-body font-bold text-[15px] text-gold-light/90 tracking-[0.5px] inline-flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" className="w-5 h-5">
            <defs><linearGradient id="lg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#F0D78C"/><stop offset="100%" stopColor="#D4A843"/></linearGradient></defs>
            <ellipse cx="32" cy="24" rx="8" ry="18" fill="url(#lg)" opacity=".95"/>
            <ellipse cx="32" cy="24" rx="8" ry="18" fill="url(#lg)" opacity=".85" transform="rotate(-30 32 38)"/>
            <ellipse cx="32" cy="24" rx="8" ry="18" fill="url(#lg)" opacity=".75" transform="rotate(-60 32 38)"/>
            <ellipse cx="32" cy="24" rx="8" ry="18" fill="url(#lg)" opacity=".85" transform="rotate(30 32 38)"/>
            <ellipse cx="32" cy="24" rx="8" ry="18" fill="url(#lg)" opacity=".75" transform="rotate(60 32 38)"/>
            <circle cx="32" cy="34" r="3.5" fill="#3B1F6E"/>
          </svg>
          spirit.charsoft.ai
        </span>
        <span className="font-body text-[13px] text-white/50">
          @revCharlene
        </span>
      </div>

      {/* Content: split layout */}
      <div className="relative z-[2] max-w-[1100px] mx-auto w-full px-6 pt-24 pb-20 flex flex-col-reverse lg:flex-row items-center gap-10 lg:gap-16">

        {/* Left: text content */}
        <div className="flex-1 text-center lg:text-left">
          <div className="inline-block py-1.5 px-5 rounded-full bg-white/[0.06] border border-gold-light/20 text-gold-light text-[11px] font-semibold tracking-[2px] uppercase mb-6">
            Spiritual Technology
          </div>

          <h1 className="font-display text-[clamp(36px,6vw,58px)] font-bold text-white leading-[1.08] mb-6">
            AI &amp; Tech for<br />Spiritual Leaders
          </h1>

          <p className="text-[clamp(16px,2vw,19px)] text-white/75 leading-relaxed mb-2 max-w-[500px] mx-auto lg:mx-0">
            I'm Rev. Charlene — award-winning IT trainer,
            AI mentor, and spiritual leader.
          </p>
          <p className="text-[clamp(15px,1.8vw,17px)] text-white/45 leading-relaxed mb-10 max-w-[500px] mx-auto lg:mx-0">
            Why should corporations have all the fun with AI?
            I build tools for spiritual leaders that free up your time —
            so you can do what matters most.
          </p>

          <div className="flex gap-4 justify-center lg:justify-start flex-wrap">
            <a
              href="#tools"
              className="py-4 px-9 rounded-full bg-gradient-to-r from-gold-warm to-gold text-white text-base font-bold no-underline shadow-[0_4px_30px_rgba(201,150,44,0.3)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_6px_40px_rgba(201,150,44,0.45)]"
            >
              See the Tools ↓
            </a>
            <a
              href="#free"
              className="py-4 px-9 rounded-full bg-white/[0.06] border border-white/15 text-white/80 text-base font-medium no-underline transition-all duration-200 hover:bg-white/10 hover:text-white hover:border-white/25"
            >
              Free Resources
            </a>
          </div>
        </div>

        {/* Right: photo */}
        <div className="relative shrink-0">
          {/* Glow behind photo */}
          <div className="absolute inset-[-30px] rounded-[40px] animate-breathe-slow z-0"
            style={{
              background: 'radial-gradient(circle, rgba(201,150,44,0.15) 0%, rgba(123,82,171,0.08) 50%, transparent 70%)',
            }}
          />
          <div className="relative">
            <img
              src="/char with app.jpg"
              alt="Rev. Charlene"
              className="relative z-[1] w-[260px] h-[325px] lg:w-[320px] lg:h-[400px] object-cover object-top rounded-[28px] shadow-[0_20px_60px_rgba(0,0,0,0.4)] scale-x-[-1]"
            />
            {/* Soft gold edge light */}
            <div className="absolute inset-0 rounded-[28px] z-[2] pointer-events-none"
              style={{
                boxShadow: 'inset 0 0 0 1px rgba(232,204,122,0.15)',
              }}
            />
            {/* Bottom fade into background */}
            <div className="absolute bottom-0 left-0 right-0 h-24 rounded-b-[28px] z-[2] pointer-events-none bg-gradient-to-t from-brand-mid/80 to-transparent" />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[2] transition-opacity duration-500"
        style={{ opacity: scrollY > 100 ? 0 : 0.5 }}
      >
        <div className="w-7 h-11 rounded-[14px] border-2 border-white/20 flex justify-center pt-2">
          <div className="w-1 h-2.5 rounded-sm bg-gold-light/60 animate-scroll-bounce" />
        </div>
      </div>
    </div>
  )
}
